import { OAuthAuthUrl, OAuthClientId } from 'const'

const PKCE_VERIFIER_KEY = 'oauth_code_verifier'
const AUTH_PROVIDER_KEY = 'auth_provider'

function base64urlEncode(array: Uint8Array): string {
	return btoa(String.fromCharCode(...array))
		.replace(/\+/g, '-')
		.replace(/\//g, '_')
		.replace(/=/g, '')
}

function generateCodeVerifier(): string {
	const array = new Uint8Array(32)
	crypto.getRandomValues(array)
	return base64urlEncode(array)
}

async function generateCodeChallenge(verifier: string): Promise<string> {
	const data = new TextEncoder().encode(verifier)
	const digest = await crypto.subtle.digest('SHA-256', data)
	return base64urlEncode(new Uint8Array(digest))
}

export default class OAuthRepository {
	private get redirectUri(): string {
		return `${window.location.origin}/oauth/callback`
	}

	async startAuthorization(): Promise<void> {
		const verifier = generateCodeVerifier()
		const challenge = await generateCodeChallenge(verifier)
		sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier)

		const params = new URLSearchParams({
			response_type: 'code',
			client_id: OAuthClientId,
			redirect_uri: this.redirectUri,
			code_challenge: challenge,
			code_challenge_method: 'S256',
		})

		window.location.href = `${OAuthAuthUrl}/oauth/authorize?${params.toString()}`
	}

	async exchangeCode(code: string): Promise<{ accessToken: string; refreshToken: string }> {
		const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY)
		if (!verifier) throw new Error('Missing PKCE code verifier')
		sessionStorage.removeItem(PKCE_VERIFIER_KEY)

		const body = new URLSearchParams({
			grant_type: 'authorization_code',
			code,
			redirect_uri: this.redirectUri,
			client_id: OAuthClientId,
			code_verifier: verifier,
		})

		const response = await fetch(`${OAuthAuthUrl}/oauth/token`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
			body: body.toString(),
		})

		if (!response.ok) {
			const text = await response.text()
			throw new Error(`Token exchange failed: ${text}`)
		}

		const data = await response.json()
		if (!data.refresh_token) throw new Error('Server did not return a refresh token')
		return {
			accessToken: data.access_token,
			refreshToken: data.refresh_token,
		}
	}

	async refreshToken(refreshToken: string): Promise<{ accessToken: string; refreshToken: string }> {
		const body = new URLSearchParams({
			grant_type: 'refresh_token',
			refresh_token: refreshToken,
			client_id: OAuthClientId,
		})

		const response = await fetch(`${OAuthAuthUrl}/oauth/token`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
			body: body.toString(),
		})

		if (!response.ok) {
			const text = await response.text()
			throw new Error(`Token refresh failed: ${text}`)
		}

		const data = await response.json()
		if (!data.refresh_token) throw new Error('Server did not return a refresh token')
		return {
			accessToken: data.access_token,
			refreshToken: data.refresh_token,
		}
	}

	async signOut(accessToken: string): Promise<void> {
		const response = await fetch(`${OAuthAuthUrl}/oauth/logout`, {
			method: 'POST',
			headers: { Authorization: `Bearer ${accessToken}` },
		})
		if (!response.ok) {
			const text = await response.text()
			let message = `Sign out failed: ${response.status}`
			try {
				message = (JSON.parse(text) as { error?: string }).error ?? message
			} catch {
				/* ignore */
			}
			throw new Error(message)
		}
	}

	static markAsOAuthProvider(): void {
		localStorage.setItem(AUTH_PROVIDER_KEY, 'oauth')
	}

	static clearProvider(): void {
		localStorage.removeItem(AUTH_PROVIDER_KEY)
	}

	static isOAuthProvider(): boolean {
		return localStorage.getItem(AUTH_PROVIDER_KEY) === 'oauth'
	}
}
